<script setup lang="ts">
  import { computed } from 'vue';

  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  const props = withDefaults(
    defineProps<{
      value?: number;
      compact?: boolean;
      options?: Intl.NumberFormatOptions;
      noTooltip?: boolean;
      placeholder?: string;
      maximumFractionDigits?: number;
      percentage?: boolean;
      monotype?: boolean;
    }>(),
    {
      maximumFractionDigits: 2,
      monotype: true,
    },
  );

  const showCompact = computed<boolean>(
    () => props.compact || (props.value !== undefined && props.value > 999999),
  );

  const expandedFormattedAmount = computed(() => {
    if (props.value !== undefined) {
      const formatter = new Intl.NumberFormat('en-US', {
        ...(props.options || {}),
        notation: 'standard',
      });

      return formatter.format(props.value);
    }

    return props.placeholder || '';
  });

  const formattedNumber = computed<string>(() => {
    if (props.value !== undefined) {
      const formatter = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: props.maximumFractionDigits,
        ...(props.options || {}),
        notation: showCompact.value ? 'compact' : 'standard',
      });

      return formatter.format(props.value);
    }

    return props.placeholder || '';
  });
</script>

<template>
  <Tooltip :disabled="!showCompact || noTooltip || value === undefined">
    <TooltipTrigger as-child>
      <span
        v-bind="$attrs"
        :title="noTooltip ? expandedFormattedAmount : ''"
        :class="monotype && 'font-mono'"
      >
        {{ formattedNumber }}<span v-if="percentage">%</span><slot />
      </span>
    </TooltipTrigger>

    <TooltipContent>
      {{ expandedFormattedAmount }}
    </TooltipContent>
  </Tooltip>
</template>

<style scoped></style>
